import { isBrowser } from "./env";

/**
 * Отложенная загрузка сторонних скриптов (SEO #12, task 4575).
 *
 * Раньше все внешние сервисы подключались тегами в общем хелмете и грузились сразу на каждой
 * странице — вместе они дают заметную часть веса и занимают основной поток на старте.
 * Здесь собраны два триггера, согласованные с маркетингом и продуктом:
 * «после полной загрузки страницы» и «после первого действия пользователя».
 */

/** Событий, по которым считаем, что пользователь начал взаимодействовать со страницей. */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"] as const;

/**
 * Выполняет задачу после события `load`.
 *
 * @param fallbackMs страховочный таймер: если `load` почему-то не наступит (зависший ресурс,
 * вкладка открыта в фоне), задача всё равно выполнится. Без него редкие визиты просто
 * не попали бы в статистику.
 */
export const runAfterWindowLoad = (task: () => void, fallbackMs = 5000): void => {
  if (!isBrowser()) {
    return;
  }

  let done = false;
  const run = () => {
    if (done) {
      return;
    }
    done = true;
    window.removeEventListener("load", run);
    task();
  };

  if (document.readyState === "complete") {
    run();
    return;
  }

  window.addEventListener("load", run);
  window.setTimeout(run, fallbackMs);
};

/** Выполняет задачу после первого действия пользователя: клик, скролл, движение мыши, ввод. */
export const runOnFirstInteraction = (task: () => void): void => {
  if (!isBrowser()) {
    return;
  }

  let done = false;
  const run = () => {
    if (done) {
      return;
    }
    done = true;
    INTERACTION_EVENTS.forEach((event) =>
      window.removeEventListener(event, run));
    task();
  };

  INTERACTION_EVENTS.forEach((event) =>
    window.addEventListener(event, run, { once: true, passive: true }));
};

/**
 * Вставляет внешний скрипт один раз. Повторный вызов с тем же `src` ничего не делает —
 * триггеры могут сработать дважды (например, `load` и страховочный таймер в редких гонках).
 */
export const injectScript = (src: string, id?: string): void => {
  if (!isBrowser() || document.querySelector(`script[src="${src}"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  if (id) {
    script.id = id;
  }
  document.head.appendChild(script);
};
